import React from 'react'

import Modal from '../../../src/components/dialogs/Modal'
import {
  Content as DialogContent,
  Dialog,
  Header as DialogHeader,
} from '../../../src/components/dialogs/SharedDialogParts'
import * as Icons from '../../../src/components/elements/Icons'
import PillButton from '../../../src/components/elements/PillButton'
import FormElement from '../../../src/components/forms/FormElement'
import {
  BasicType,
  CommonFormElementProps,
  ElementState,
  ElementType,
  Field,
  QuestionType,
} from '../../../src/components/forms/FormElementParts'
import MultiselectQuestion from '../../../src/components/forms/MultiselectQuestion'
import * as validationFns from '../../../src/components/forms/Validations'
import styled, { MQ_TABLET } from '../../../src/constants/theme'

export const getModalOpenName = (field: Field) => `${field.name}ModalOpen`

export interface Form {
  fields: Field[]
  header?: string
  memberIdOverride?: string
  type: FormIdentifier
}

export enum FormIdentifier {
  CHANGE_PASSWORD = 'ChangePassword',
  EMAIL = 'Email',
  FORGOT_PASSWORD = 'ForgotPassword',
  MEMBER_ID = 'MemberID',
  OPEN_REGISTRATION = 'OpenRegistration',
  REGISTER_INVITATION = 'RegisterInvitation',
  REGISTRATION_DEFAULT = 'RegistrationDefault',
  RESEND_INVITATION = 'ResendInvitation',
  SIGN_IN = 'SignIn',
  SUCCESS = 'Success',
  VERIFY_TOKEN = 'VerifyToken',
}

export interface ServerError {
  errors?: { [key: string]: string } | {}
  message?: string | null
}

export const replaceLabel = (label: string, onReplaceLabel: any) => {
  if (onReplaceLabel) {
    return onReplaceLabel(label)
  }
  return label
}

export const JustifiedDiv = styled.div<{ justify?: string }>(
  {
    '& a': {
      '&:hover': {
        textDecoration: 'none',
      },
      textDecoration: 'underline',
    },
    '& h2': {
      fontSize: 26,
      fontWeight: 'bold',
      lineHeight: 1.2,
      marginBottom: 15,
    },
    marginBottom: 10,
    marginTop: 10,
  },
  ({ justify, theme }) => ({
    ...MQ_TABLET({
      '& button + button': {
        marginLeft: theme.padding,
      },
    }),
    '& button + button': {
      marginLeft: theme.paddingMobile,
    },
    textAlign: justify || 'left',
  }),
)

const DialogImg = styled.img({
  maxWidth: '100%',
})

const RightButton = styled.button({
  '&:focus': {
    outline: ['1px dotted #212121', '5px auto -webkit-focus-ring-color'],
  },
})

interface Props {
  fields: Field[]
  formType?: FormIdentifier
  isValid?: boolean
  onChange?: any
  onClickBack?: () => void
  onCloseModal?: (field: Field) => any
  onOpenModal?: (field: Field) => any
  onReplaceLabel?: (label: string) => string
  serverError?: ServerError
  state?: { [key: string]: boolean }
}

export default ({
  fields,
  formType,
  isValid,
  onChange,
  onClickBack,
  onCloseModal,
  onOpenModal,
  onReplaceLabel,
  serverError,
  state,
}: Props) => {
  const renderBooleanField = commonProps => (
    <FormElement {...commonProps} validations={[validationFns.boolean]} />
  )

  const renderPlainTextField = (field: Field, key: string) => (
    <JustifiedDiv
      justify={field.justify}
      dangerouslySetInnerHTML={{
        __html: replaceLabel(field.label, onReplaceLabel),
      }}
      key={key}
    />
  )

  const renderSubmitButton = (field: Field, key: string) => {
    return formType === FormIdentifier.EMAIL ? (
      <JustifiedDiv justify="center" key={key}>
        <PillButton
          className="big"
          disabled={!isValid || serverError?.message !== null}
          type="submit"
        >
          {replaceLabel(field.label, onReplaceLabel)}
        </PillButton>
        <PillButton
          className="big asOutline"
          type="button"
          onClick={onClickBack}
        >
          Back
        </PillButton>
      </JustifiedDiv>
    ) : (
      <JustifiedDiv justify="center" key={key}>
        <PillButton
          className="big"
          disabled={!isValid || serverError?.message !== null}
          type="submit"
        >
          {replaceLabel(field.label, onReplaceLabel)}
        </PillButton>
      </JustifiedDiv>
    )
  }

  const renderEmailField = (commonProps: CommonFormElementProps) => (
    <FormElement {...commonProps} validations={[validationFns.email]} />
  )

  const renderOptionsField = (
    commonProps: CommonFormElementProps,
    field: Field,
  ) => (
    <FormElement
      {...commonProps}
      display={field.display}
      options={field.options}
    />
  )

  const renderPhoneField = (commonProps: CommonFormElementProps) => (
    <FormElement
      {...commonProps}
      validations={[validationFns.maxLength(10), validationFns.minLength(10)]}
    />
  )

  const renderZipField = (commonProps: CommonFormElementProps) => (
    <FormElement
      {...commonProps}
      validations={[validationFns.maxLength(5), validationFns.minLength(5)]}
    />
  )

  const renderMultiselectQuestion = (
    commonProps: CommonFormElementProps,
    field: Field,
  ) => {
    return (
      <MultiselectQuestion
        key={commonProps.key}
        commonProps={commonProps}
        field={field}
      />
    )
  }

  const renderDefaultField = (
    commonProps: CommonFormElementProps,
    field: Field,
  ) => {
    const validations = [validationFns.minLength(1)]
    if (field.validPattern) {
      validations.push(validationFns.regExp(new RegExp(field.validPattern)))
    }
    if (field.hint) {
      const { header, image, text } = field.hint
      return (
        <React.Fragment key={commonProps.key}>
          <FormElement {...commonProps} validations={validations} />
          <Modal
            isOpen={!!(state && state[getModalOpenName(field)])}
            onRequestClose={onCloseModal && onCloseModal(field)}
          >
            <Dialog>
              <DialogHeader>{header}</DialogHeader>
              <DialogContent style={{ textAlign: 'center' }}>
                {image && <DialogImg alt={``} src={image} />}
                {text && <div>{text}</div>}
              </DialogContent>
            </Dialog>
          </Modal>
        </React.Fragment>
      )
    }
    return <FormElement {...commonProps} validations={validations} />
  }

  const renderRightButton = (field: Field) => {
    return (
      <RightButton onClick={onOpenModal && onOpenModal(field)} type="button">
        <Icons.QuestionIcon />
      </RightButton>
    )
  }

  const renderField = (field: Field) => {
    const Icon = Icons[`${field.icon}Icon`]
    const key = `${field.type}_${field.label}`
    const serverErrors = serverError?.errors
    const commonProps: CommonFormElementProps = {
      description: field.description,
      elementState: ElementState.INDETERMINATE,
      icon: Icon ? <Icon /> : undefined,
      isRequired: field.required ?? true,
      key,
      label: replaceLabel(field.label, onReplaceLabel),
      name: field.name,
      onChange: onChange,
      rightButton: field.hint ? renderRightButton(field) : undefined,
      serverErrors,
      type: field.type,
    }
    switch (field.type) {
      case BasicType.PLAINTEXT:
        return renderPlainTextField(field, key)
      case BasicType.SUBMIT:
        return renderSubmitButton(field, key)
      case ElementType.EMAIL:
        return renderEmailField(commonProps)
      case ElementType.RADIO:
      case ElementType.SELECT:
        return renderOptionsField(commonProps, field)
      case ElementType.TEL:
        return renderPhoneField(commonProps)
      case ElementType.ZIP:
        return renderZipField(commonProps)
      case ElementType.CHECKBOX:
      case ElementType.SWITCH:
        return renderBooleanField(commonProps)
      case ElementType.DATE:
      case ElementType.PASSWORD:
      case ElementType.TEXT:
      case ElementType.TEXTAREA:
        return renderDefaultField(commonProps, field)
      case QuestionType.MULTISELECT:
        return renderMultiselectQuestion(commonProps, field)
      case BasicType.FOOTER:
      default:
        return null
    }
  }

  return (
    <React.Fragment>{fields.map((f: Field) => renderField(f))}</React.Fragment>
  )
}
